import Image from 'components/Common/Image'
import ImageHero from 'components/Common/ImageHero'
import HeadData from 'components/Common/HeadData'
import OfferList from 'components/OfferList/OfferList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { getStepsHowItWorks, getStepsBuyNow, getTermsOfUseLink } from './WaaveLandingPageData'
import { connect } from 'react-redux'
import Error404Page from 'components/Pages/Error404Page'
import TextButton from 'LuxKit/Button/TextButton'
import { isWaaveBpEnabled } from 'lib/payment/waaveBpUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'

const HowItWorksContent = styled.div`
  margin-top: ${rem(40)};
  width: 100%;
`

const BuyNowContent = styled.div`
  margin-top: ${rem(48)};
  width: 100%;
`

const AppStoreContent = styled.div`
  margin-top: ${rem(20)};
  width: 100%;
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};

    &.large-margin {
      margin-top: ${rem(92)};
    }
  }
`

const BankLogoSubSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};

    &.large-margin {
      margin-top: ${rem(92)};
    }
  }
`

const BankInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};

    &.large-margin {
      margin-top: ${rem(92)};
    }
  }
`

const InnerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(32)} ${rem(40)};
  max-width: ${rem(1160)};
  width: 100%;
  height: 100%;

  ${mediaQueryUp.desktop} {
    padding: ${rem(60)} ${rem(32)};
  }
`

const HeroImage = styled(ImageHero)`
  max-height: ${rem(480)};
`

const LogoImage = styled(Image)`
  margin-bottom: ${rem(24)};
  image-rendering: -webkit-optimize-contrast;
  transform: translateZ(0);
`

const BankLogo = styled(Image)`
  margin-bottom: ${rem(3)};
  image-rendering: -webkit-optimize-contrast;
  display: inline;
  transform: translateZ(0);
  max-width: 100%;
  text-align: center
`

const PoweredByLogoImage = styled(Image)`
  margin-top: ${rem(48)};
  image-rendering: -webkit-optimize-contrast;
  transform: translateZ(0);
`

const StoreImage = styled(Image)`
  margin-top: ${rem(20)};
  margin-right: ${rem(6)};
  margin-left: ${rem(6)};
`

const HeroSectionContent = styled(InnerSection)`
  align-items: center;
  justify-content: center;
  position: relative;
`

const Section = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  max-width: 100%;

  &:nth-child(even) {
    background-color: ${props => props.theme.palette.neutral.default.five};
  }
`

const SectionCTA = styled(TextButton)`
  margin-top: ${rem(32)};
`

const SectionBlurb = styled(BodyText).attrs<any>({
  variant: 'large',
  align: 'center',
  colour: 'neutral-eight',
})`
  margin-top: ${rem(16)};
  max-width: ${rem(750)};
  width: 100%;
`

const ProductDescriptors = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    margin-left: -${rem(30)};
  }
`

const ProductDescriptor = styled.div`
  display: flex;
  flex-direction: column;
  /* Fixes IE overflow issue */
  align-items: stretch;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(24)};
  margin-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    margin-left: ${rem(30)};
    margin-top: 0;
    height: ${rem(180)};
    flex: 1 1 33%;
  }
`

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  > * + * {
    margin-top: ${rem(16)};

    ${mediaQueryUp.tablet} {
      margin-top: 0;
    }
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    margin-left: -${rem(32)};
  }
`

const HowItWorksCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  padding: ${rem(110)} ${rem(32)} ${rem(32)};
  max-width: ${rem(415)};
  flex-grow: 1;
  margin-left: ${rem(32)};

  ${mediaQueryUp.tablet} {
    flex-basis: 100%;
  }
`

const AppLinks = styled(TextLink)`
  display: block;
  padding: 0;
`

const HowItWorksCardImage = styled.div`
  position: absolute;
  top: ${rem(40)};
  left: 0;
  right: 0;
  height: ${rem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
`
interface Props {
  currency: string
  theme: any
  isAtomeBpEnabled: boolean
}

function WaaveLandingPage(props: Props) {
  const intl = useIntl()

  useEffect(() => {
    window.scroll(0, 0)
  }, [])

  const {
    currency,
    isAtomeBpEnabled,
  } = props

  const stepsHowItWorks = getStepsHowItWorks(intl) || []
  const stepsBuyNow = getStepsBuyNow(intl) || []
  const termsOfUseLink = getTermsOfUseLink(currency)
  const luxPayLogoAlt = intl.formatMessage({
    id: 'VAQHASW6kFsNIJtmU9Qd',
    defaultMessage: 'LuxPay',
    description: 'Alt text for the LuxPay logo shown on the LuxPay promotional landing page',
  })

  if (!isAtomeBpEnabled) {
    return <Error404Page invalidRegion />
  }

  return (
    <div>
      <HeadData title={intl.formatMessage({
        id: 'Kl1paxmaFiGFUw8rv_vs',
        defaultMessage: 'LuxPay',
        description: 'Page title for the LuxPay promotional landing page',
      })}
      />
      <HeroImage
        alt=""
        aria-hidden="true"
        gravity="auto"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="16:5"
        id="waave-landing-page-background-logo"
        brightness={-30}
      >
        <HeroSectionContent>
          <LogoImage
            height="60"
            id="luxpay-landing-page-logo"
            alt={luxPayLogoAlt}
            highRes
            format="svg"
            quality="good"
          />
          <Heading variant="heading2" align="center" colour="neutral-eight">
            {intl.formatMessage({
              id: '4ogVCFsNYms4q9p65PbY',
              defaultMessage: 'A fast, simple and secure way to pay using your bank',
              description: 'Hero heading on Waave/LuxPay landing page on the promotional landing page',
            })}
          </Heading>
          <SectionBlurb variant="subtitle2" colour="neutral-eight">
            {intl.formatMessage({
              id: 'mmSj-ddmEC-pCoaLOTdA',
              defaultMessage: 'No card details. No minimum spend.',
              description: 'Hero blurb on Waave/LuxPay landing page on the promotional landing page',
            })}
          </SectionBlurb>
          <PoweredByLogoImage
            height="40"
            id="powered-by-waave-landing-page-logo"
            alt={luxPayLogoAlt}
            highRes
            format="svg"
            quality="good"
          />
        </HeroSectionContent>
      </HeroImage>
      <Section>
        <InnerSection>
          <Heading variant="heading2" align="center">
            {intl.formatMessage({
              id: 'NFvjmikYn41IKHmiEXku',
              defaultMessage: 'Meet Australia’s new way to pay: LuxPay, powered by Waave',
              description: 'Section heading introducing LuxPay and Waave on landing page',
            })}
          </Heading>
          <HowItWorksContent>
            <ColumnContainer>
              {stepsHowItWorks.map((step, index) => (
                <HowItWorksCard key={index}>
                  <HowItWorksCardImage>{step.image}</HowItWorksCardImage>
                  <BodyText variant="large" weight="bold">{step.title}</BodyText>
                  <BodyText variant="medium">
                    {step.description}
                  </BodyText>
                </HowItWorksCard>
              ))}
            </ColumnContainer>
          </HowItWorksContent>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3">
            <FormattedMessage
              id="gVJCR2f5vqUZRaLJXl9z"
              defaultMessage="LuxPay<sup>*</sup> your next Luxury Escapes"
              description="Heading promoting LuxPay as a payment option for Luxury Escapes bookings"
              values={{ sup: (chunks) => <sup>{chunks}</sup> }}
            />
          </Heading>
          <BuyNowContent>
            <ProductDescriptors>
              {stepsBuyNow.map((step, index) => (
                <ProductDescriptor key={index}>
                  <Heading variant="heading3">{step.title}</Heading>
                  <BodyText variant="medium">{step.description}</BodyText>
                </ProductDescriptor>
              ))}
            </ProductDescriptors>
          </BuyNowContent><br />
          <BodyText variant="medium">
            <FormattedMessage
              id="Yodr1qQx1bGFLKmJmBu-"
              defaultMessage="<sup>*</sup>LuxPay utilises a third-party service provider. By paying with LuxPay, the customer acknowledges and agrees to the <link>terms of use</link>."
              description="LuxPay third-party service disclaimer with terms of use link"
              values={{
                sup: (chunks) => <sup>{chunks}</sup>,
                link: (chunks) => <TextLink weight="regular" target="_blank" href={termsOfUseLink}><BodyText variant="small" align="center">{chunks}</BodyText></TextLink>,
              }}
            />
          </BodyText>
          <BankInfoSection>
            <Heading variant="heading3">
              {intl.formatMessage({
                id: 'KkkDeuO6fefABk8pbTri',
                defaultMessage: 'Available with every Australian Bank',
                description: 'Section heading for Australian bank availability on Waave/LuxPay landing page',
              })}
            </Heading>
            <BankLogoSubSection>
              <BankLogo id="waave-bank6"></BankLogo>
              <BankLogo id="waave-bank7"></BankLogo>
              <BankLogo id="waave-bank8"></BankLogo>
              <BankLogo id="waave-bank3"></BankLogo>
              <BankLogo id="waave-bank2"></BankLogo>
              <BankLogo id="waave-bank5"></BankLogo>
              <BankLogo id="waave-bank1"></BankLogo>
              <BankLogo id="waave-bank4"></BankLogo>
            </BankLogoSubSection>
          </BankInfoSection>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3" align="center">
            {intl.formatMessage({
              id: 'gXIrL0x8w_dNmcl9v8ee',
              defaultMessage: 'Use the Waave app to LuxPay!',
              description: 'Section heading for Waave app on LuxPay landing page',
            })}
          </Heading>
          <AppStoreContent>
            <BodyText variant="large" align="center">
              {intl.formatMessage({
                id: 'bCHLxIydgApx3VqCLafu',
                defaultMessage: 'Manage your existing orders with the easy-to-use app.',
                description: 'Body text for Waave app section on LuxPay landing page',
              })}
            </BodyText>
            <ColumnContainer>
              <AppLinks href="https://apps.apple.com/au/app/waave/id1623119005" target="_blank" rel="noopener">
                <StoreImage id="qy1f0pkl6sbukmkbwayf" height="48" quality="good" format="svg" />
              </AppLinks>
              <AppLinks href="https://play.google.com/store/apps/details?id=com.waave.wallet" target="_blank" rel="noopener">
                <StoreImage id="p0qt65l8f6tzobhdkh" height="48" quality="good" format="svg" />
              </AppLinks>
            </ColumnContainer>
          </AppStoreContent>

        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <SubSection>
            <BodyText variant="large" align="center" as="h2">
              {intl.formatMessage({
                id: 'GFzSXFUakyS05nEFxOKu',
                defaultMessage: 'Deals of the week',
                description: 'Section heading for deals on Waave/LuxPay landing page',
              })}
            </BodyText>
            <BuyNowContent>
              <OfferList
                maxOffers={4}
                filters={{
                  offerTypes: FLASH_OFFERS_TYPES,
                  frontPageVisibleOnly: true,
                }}
              />
            </BuyNowContent>
            <SectionCTA kind="primary" href="/">
              {intl.formatMessage({
                id: 'P5V0-9xtw0vZEMDljhXb',
                defaultMessage: 'Discover all amazing deals',
                description: 'Button text to discover all deals on Waave/LuxPay landing page',
              })}
            </SectionCTA>
          </SubSection>
        </InnerSection>
      </Section>
    </div>
  )
}

const mapStateToProps = (state: App.State) => {
  const currency = state.geo.currentCurrency

  return {
    currency,
    isAtomeBpEnabled: isWaaveBpEnabled(currency),
  }
}

export default connect(mapStateToProps)(WaaveLandingPage)
